import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useUser } from '@clerk/clerk-react';
import { useAuthStore } from '@/stores/authStore';
import { useWorkspaceStore, selectActiveWorkspace } from '@/stores/workspaceStore';
import { useUIStore } from '@/stores/uiStore';
import { useSync } from '@/hooks/useSync';
import { pluginsApi } from '@/lib/api';
import { db, clearWorkspaceData } from '@/lib/db';
import AppShell from '@/components/layout/AppShell';
import SyncStatus from '@/components/ui/SyncStatus';
import type { Plugin } from '@/types';

// ─── Section wrapper ──────────────────────────────────────────────────────────

const Section: React.FC<{ title: string; description?: string; children: React.ReactNode }> = ({
  title,
  description,
  children,
}) => (
  <div className="bg-white dark:bg-surface-900 rounded-xl border border-surface-200 dark:border-surface-800 shadow-card overflow-hidden">
    <div className="p-5 border-b border-surface-100 dark:border-surface-800">
      <h3 className="text-sm font-semibold text-surface-900 dark:text-white">{title}</h3>
      {description && <p className="text-xs text-surface-500 mt-0.5">{description}</p>}
    </div>
    <div className="p-5">{children}</div>
  </div>
);

// ─── Toggle ───────────────────────────────────────────────────────────────────

const Toggle: React.FC<{
  label: string;
  description?: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}> = ({ label, description, checked, onChange }) => (
  <div className="flex items-center justify-between gap-4">
    <div>
      <p className="text-sm font-medium text-surface-700 dark:text-surface-300">{label}</p>
      {description && <p className="text-xs text-surface-400 mt-0.5">{description}</p>}
    </div>
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className={[
        'relative inline-flex h-5 w-9 items-center rounded-full transition-colors flex-shrink-0',
        checked ? 'bg-primary-600' : 'bg-surface-300 dark:bg-surface-600',
      ].join(' ')}
    >
      <span
        className={[
          'inline-block h-3.5 w-3.5 rounded-full bg-white shadow transform transition-transform',
          checked ? 'translate-x-4.5' : 'translate-x-0.5',
        ].join(' ')}
      />
    </button>
  </div>
);

// ─── SettingsPage ─────────────────────────────────────────────────────────────

const SettingsPage: React.FC = () => {
  const { user: clerkUser } = useUser();
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const updateUser = useAuthStore((s) => s.updateUser);

  const activeWorkspace = useWorkspaceStore(selectActiveWorkspace);
  const workspaceId = activeWorkspace?.id ?? null;

  const theme = useUIStore((s) => s.theme);
  const setTheme = useUIStore((s) => s.setTheme);
  const addToast = useUIStore((s) => s.addToast);

  const { status: syncStatus, lastSyncedAt, triggerSync, isOffline } = useSync();

  const [editName, setEditName] = useState(user?.name ?? '');
  const [editBio, setEditBio] = useState(user?.bio ?? '');
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [activeTab, setActiveTab] = useState<'profile' | 'workspace' | 'plugins' | 'sync'>('profile');

  // Load plugins
  const { data: plugins = [], isLoading: pluginsLoading } = useQuery({
    queryKey: ['plugins', workspaceId],
    queryFn: async () => {
      if (!workspaceId) {
        return [];
      }
      const data = await pluginsApi.list(workspaceId);
      await db.plugins.bulkPut(data);
      return data;
    },
    enabled: Boolean(workspaceId) && activeTab === 'plugins',
    staleTime: 60_000,
  });

  const handleSaveProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSavingProfile(true);
    try {
      // Persist the name to Clerk so it survives page reloads
      if (clerkUser) {
        await clerkUser.update({ firstName: editName, lastName: '' });
      }
      // Also update local store immediately so the UI reflects the change
      updateUser({ name: editName, bio: editBio });
      addToast({ title: 'Profile saved', variant: 'success' });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to save';
      addToast({ title: 'Error', description: message, variant: 'error' });
    } finally {
      setIsSavingProfile(false);
    }
  };

  const handleLogout = () => {
    logout();
  };

  const tabs = [
    { id: 'profile', label: 'Profile' },
    { id: 'workspace', label: 'Workspace' },
    { id: 'plugins', label: 'Plugins' },
    { id: 'sync', label: 'Sync & Storage' },
  ] as const;

  return (
    <AppShell>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 pb-12 space-y-6">
            {/* Header */}
            <div>
              <h1 className="text-2xl font-bold text-surface-900 dark:text-white">Settings</h1>
              <p className="text-sm text-surface-500 mt-0.5">Manage your account, workspace, and preferences</p>
            </div>

            {/* Tabs */}
            <div className="flex gap-1 bg-surface-100 dark:bg-surface-800 p-1 rounded-xl w-fit">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id)}
                  className={[
                    'px-4 py-1.5 rounded-lg text-sm font-medium transition-all',
                    activeTab === tab.id
                      ? 'bg-white dark:bg-surface-700 text-primary-700 dark:text-primary-300 shadow-card'
                      : 'text-surface-500 hover:text-surface-700 dark:hover:text-surface-300',
                  ].join(' ')}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Profile tab */}
            {activeTab === 'profile' && (
              <div className="space-y-5">
                <Section title="Personal information" description="Your name and bio visible to workspace members">
                  <form onSubmit={handleSaveProfile} className="space-y-4">
                    <div>
                      <label className="text-xs font-medium text-surface-600 dark:text-surface-400 block mb-1.5">
                        Full name
                      </label>
                      <input
                        type="text"
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        className="w-full rounded-lg border border-surface-300 dark:border-surface-600 bg-white dark:bg-surface-900 text-sm px-3 py-2.5 outline-none focus:border-primary-500"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-surface-600 dark:text-surface-400 block mb-1.5">
                        Email
                      </label>
                      <input
                        type="email"
                        value={user?.email ?? ''}
                        disabled
                        className="w-full rounded-lg border border-surface-200 dark:border-surface-700 bg-surface-50 dark:bg-surface-800 text-sm px-3 py-2.5 text-surface-400 cursor-not-allowed"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-surface-600 dark:text-surface-400 block mb-1.5">
                        Bio
                      </label>
                      <textarea
                        value={editBio}
                        onChange={(e) => setEditBio(e.target.value)}
                        rows={3}
                        placeholder="Tell us about yourself…"
                        className="w-full rounded-lg border border-surface-300 dark:border-surface-600 bg-white dark:bg-surface-900 text-sm px-3 py-2.5 outline-none focus:border-primary-500 resize-none"
                      />
                    </div>
                    <div className="flex justify-end">
                      <button
                        type="submit"
                        disabled={isSavingProfile}
                        className="px-4 py-2 text-sm font-medium text-white bg-primary-700 hover:bg-primary-600 rounded-lg transition-colors disabled:opacity-60"
                      >
                        {isSavingProfile ? 'Saving…' : 'Save profile'}
                      </button>
                    </div>
                  </form>
                </Section>

                <Section title="Appearance">
                  <div className="space-y-3">
                    <p className="text-xs font-medium text-surface-600 dark:text-surface-400">Theme</p>
                    <div className="grid grid-cols-3 gap-2">
                      {(['light', 'dark', 'system'] as const).map((t) => (
                        <button
                          key={t}
                          type="button"
                          onClick={() => setTheme(t)}
                          className={[
                            'px-3 py-2 rounded-lg border text-sm font-medium transition-all capitalize',
                            theme === t
                              ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                              : 'border-surface-200 dark:border-surface-700 text-surface-600 dark:text-surface-400 hover:border-primary-300',
                          ].join(' ')}
                        >
                          {t === 'light' ? 'Light' : t === 'dark' ? 'Dark' : 'System'}
                        </button>
                      ))}
                    </div>
                  </div>
                </Section>

                <Section title="Notifications">
                  <div className="space-y-4">
                    <Toggle
                      label="Email notifications"
                      description="Receive updates via email"
                      checked={user?.preferences.emailNotifications ?? true}
                      onChange={(v) =>
                        updateUser({ preferences: { ...user!.preferences, emailNotifications: v } })
                      }
                    />
                    <Toggle
                      label="AI suggestions"
                      description="Enable AI-powered capability recommendations"
                      checked={user?.preferences.aiSuggestionsEnabled ?? true}
                      onChange={(v) =>
                        updateUser({ preferences: { ...user!.preferences, aiSuggestionsEnabled: v } })
                      }
                    />
                  </div>
                </Section>

                <Section title="Danger zone">
                  <button
                    type="button"
                    onClick={handleLogout}
                    className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-red-600 dark:text-red-400 border border-red-200 dark:border-red-800 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                  >
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                      <polyline points="16 17 21 12 16 7" />
                      <line x1="21" y1="12" x2="9" y2="12" />
                    </svg>
                    Sign out
                  </button>
                </Section>
              </div>
            )}

            {/* Workspace tab */}
            {activeTab === 'workspace' && (
              <div className="space-y-5">
                {activeWorkspace ? (
                  <>
                    <Section title="Workspace details">
                      <div className="space-y-4">
                        <div>
                          <p className="text-xs font-medium text-surface-600 dark:text-surface-400 mb-1.5">Name</p>
                          <input
                            type="text"
                            defaultValue={activeWorkspace.name}
                            className="w-full rounded-lg border border-surface-300 dark:border-surface-600 bg-white dark:bg-surface-900 text-sm px-3 py-2.5 outline-none focus:border-primary-500"
                          />
                        </div>
                        <div>
                          <p className="text-xs font-medium text-surface-600 dark:text-surface-400 mb-1.5">Description</p>
                          <textarea
                            defaultValue={activeWorkspace.description ?? ''}
                            rows={3}
                            className="w-full rounded-lg border border-surface-300 dark:border-surface-600 bg-white dark:bg-surface-900 text-sm px-3 py-2.5 outline-none focus:border-primary-500 resize-none"
                          />
                        </div>
                      </div>
                    </Section>

                    <Section title="AI provider" description="Choose the AI model powering suggestions and search">
                      <div className="grid grid-cols-2 gap-2">
                        {(['openai', 'anthropic', 'local'] as const).map((provider) => (
                          <button
                            key={provider}
                            type="button"
                            className={[
                              'px-3 py-2.5 rounded-lg border text-sm font-medium transition-all text-left',
                              activeWorkspace.settings.aiProvider === provider
                                ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                                : 'border-surface-200 dark:border-surface-700 text-surface-600 dark:text-surface-400',
                            ].join(' ')}
                          >
                            {provider === 'openai' ? 'OpenAI' : provider === 'anthropic' ? 'Anthropic' : 'Local'}
                          </button>
                        ))}
                      </div>
                    </Section>
                  </>
                ) : (
                  <div className="text-center py-12">
                    <p className="text-sm text-surface-500">No workspace selected</p>
                  </div>
                )}
              </div>
            )}

            {/* Plugins tab */}
            {activeTab === 'plugins' && (
              <div className="space-y-4">
                <Section title="Installed plugins" description="Extend CapOS with integrations and custom capabilities">
                  {pluginsLoading ? (
                    <div className="space-y-3">
                      {Array.from({ length: 3 }).map((_, i) => (
                        <div key={i} className="h-14 rounded-lg bg-surface-100 dark:bg-surface-800 animate-pulse" />
                      ))}
                    </div>
                  ) : plugins.length === 0 ? (
                    <p className="text-sm text-surface-400 text-center py-6">No plugins installed</p>
                  ) : (
                    <div className="space-y-3">
                      {plugins.map((plugin: Plugin) => (
                        <div
                          key={plugin.id}
                          className="flex items-center gap-3 p-3 rounded-lg border border-surface-200 dark:border-surface-700"
                        >
                          <div className="w-9 h-9 rounded-lg bg-surface-100 dark:bg-surface-800 flex items-center justify-center flex-shrink-0">
                            {plugin.iconUrl ? (
                              <img src={plugin.iconUrl} alt="" className="w-6 h-6 rounded" />
                            ) : (
                              <svg className="w-5 h-5 text-surface-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true"><path d="M18.364 5.636a9 9 0 1 1-12.728 0"/><path d="M12 2v6"/></svg>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-surface-900 dark:text-white truncate">
                              {plugin.name}
                            </p>
                            <p className="text-xs text-surface-400">{plugin.version} · {plugin.author}</p>
                          </div>
                          <span
                            className={[
                              'px-2 py-0.5 rounded-full text-xs font-medium',
                              plugin.status === 'active'
                                ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300'
                                : plugin.status === 'error'
                                ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300'
                                : 'bg-surface-100 text-surface-500',
                            ].join(' ')}
                          >
                            {plugin.status}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </Section>
              </div>
            )}

            {/* Sync tab */}
            {activeTab === 'sync' && (
              <div className="space-y-5">
                <Section title="Sync status" description="Monitor and control data synchronisation">
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-surface-700 dark:text-surface-300">
                          Sync status
                        </p>
                        {lastSyncedAt && (
                          <p className="text-xs text-surface-400 mt-0.5">
                            Last synced: {new Date(lastSyncedAt).toLocaleString()}
                          </p>
                        )}
                      </div>
                      <SyncStatus />
                    </div>

                    <button
                      type="button"
                      onClick={triggerSync}
                      disabled={syncStatus === 'syncing' || isOffline}
                      className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-primary-700 dark:text-primary-300 border border-primary-300 dark:border-primary-700 rounded-lg hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <svg
                        className={`w-4 h-4 ${syncStatus === 'syncing' ? 'animate-spin' : ''}`}
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        aria-hidden="true"
                      >
                        <path d="M21 2v6h-6" /><path d="M3 12a9 9 0 0 1 15-6.7L21 8" />
                        <path d="M3 22v-6h6" /><path d="M21 12a9 9 0 0 1-15 6.7L3 16" />
                      </svg>
                      {syncStatus === 'syncing' ? 'Syncing…' : 'Sync now'}
                    </button>

                    {isOffline && (
                      <p className="text-xs text-yellow-600 dark:text-yellow-400">
                        You are offline. Changes will sync when connection is restored.
                      </p>
                    )}
                  </div>
                </Section>

                <Section title="Local storage" description="Data cached in your browser for offline use">
                  <div className="space-y-3 text-sm text-surface-600 dark:text-surface-400">
                    <p>Capabilities, graph nodes/edges, and collections are stored locally using IndexedDB for offline access.</p>
                    <button
                      type="button"
                      onClick={async () => {
                        if (workspaceId) {
                          await clearWorkspaceData(workspaceId);
                          addToast({ title: 'Local cache cleared', variant: 'success' });
                        }
                      }}
                      className="text-xs text-red-500 hover:text-red-600 hover:underline"
                    >
                      Clear local cache for this workspace
                    </button>
                  </div>
                </Section>
              </div>
            )}
      </div>
    </AppShell>
  );
};

export default SettingsPage;
